/**
 * Colleague Selector Component
 *
 * Grid of expert colleagues to choose from.
 */
import React from 'react';
import { Card, Typography, Row, Col, Divider, Button } from 'antd';
import { RobotOutlined } from '@ant-design/icons';
import type { ColleagueId } from '@/hooks/ai-colleagues';
import styles from './ColleagueSelector.module.less';

const { Text, Paragraph } = Typography;

export type ColleagueInfo = {
  id: ColleagueId;
  name: string;
  role: string;
  description: string;
  icon: string;
  expertise: string[];
};

const COLLEAGUES: ColleagueInfo[] = [
  {
    id: 'alex',
    name: 'Ivan',
    role: 'MLRO & Compliance Officer',
    description: 'MLRO, JFIU liaison, STR filing, AML/CFT investigations',
    icon: '🛡️',
    expertise: ['STR/JFIU', 'Case investigations', 'Regulatory reporting']
  },
  {
    id: 'morgan',
    name: 'Simpson',
    role: 'KYT & Custody Specialist',
    description: 'Cryptocurrency screening, blockchain forensics, custody operations',
    icon: '🔗',
    expertise: ['KYT screening', 'Wallet ops', 'On-chain analysis']
  },
  {
    id: 'jordan',
    name: 'Jordan',
    role: 'Payments & Pricing Specialist',
    description: 'Fiat/crypto payments, fee schedules, pricing, reconciliation',
    icon: '💰',
    expertise: ['Payment processing', 'Fee schedules', 'Pricing setup']
  },
  {
    id: 'casey',
    name: 'Leon',
    role: 'Cards & Issuing Specialist',
    description: 'Card applications, issuing operations, disputes, chargebacks',
    icon: '💳',
    expertise: ['Card issuing', 'Disputes', 'Card programs']
  },
  {
    id: 'sam',
    name: 'Carla',
    role: 'Client Services Lead',
    description: 'Support tickets, client communications, service quality',
    icon: '💬',
    expertise: ['Client responses', 'Ticket management', 'Escalations']
  },
  {
    id: 'riley',
    name: 'Riley',
    role: 'Operations Lead',
    description: 'Platform operations, user management, general guidance',
    icon: '⚙️',
    expertise: ['User management', 'Process guidance', 'General ops']
  },
  {
    id: 'taylor',
    name: 'Karl',
    role: 'Trust Admin Specialist',
    description: 'Trust structures, fund administration, corporate accounts',
    icon: '🏛️',
    expertise: ['Trust accounts', 'CRS/FATCA', 'Fund admin']
  },
];

type ColleagueSelectorProps = {
  onSelect: (colleagueId: ColleagueId) => void;
  selectedId?: ColleagueId;
};

export const ColleagueSelector: React.FC<ColleagueSelectorProps> = ({ onSelect, selectedId }) => {
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text strong style={{ fontSize: 16 }}>Choose your Digital Staff expert</Text>
        <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 16 }}>
          Each team member is an expert in their domain with knowledge of HK regulations
        </Paragraph>
      </div>

      <Row gutter={[12, 12]}>
        {COLLEAGUES.map((colleague) => (
          <Col key={colleague.id} xs={12} sm={8}>
            <Card
              hoverable
              className={`${styles.colleagueCard} ${selectedId === colleague.id ? styles.selected : ''}`}
              onClick={() => onSelect(colleague.id)}
              styles={{ body: { padding: 16 } }}
            >
              <div className={styles.icon}>{colleague.icon}</div>
              <Text strong className={styles.name}>{colleague.name}</Text>
              <Text type="secondary" className={styles.role}>{colleague.role}</Text>
            </Card>
          </Col>
        ))}
      </Row>

      <Divider plain>OR</Divider>

      <Button
        type="default"
        icon={<RobotOutlined />}
        block
        size="large"
        className={styles.generalButton}
        onClick={() => onSelect('general')}
      >
        General Assistant - I'll route to the right expert
      </Button>
    </div>
  );
};

export { COLLEAGUES };
export default ColleagueSelector;
